/**
 * The workflow graph, in the browser: run order, ancestry, and what the hand
 * is allowed to draw.
 *
 * ── This is a second implementation of
 * server/src/services/delivery/graphTypes.ts, and that is a thing worth being
 * uncomfortable about. The two workspaces share no code — only hand-mirrored
 * types — so the server's version cannot be imported here without coupling the
 * browser build to the server sources. What keeps them from drifting is
 * shared/graphCases.json: a table of graphs and their expected ordering and
 * problems that a test in EACH workspace runs against its own implementation.
 * Change the behaviour of either and add a case, or one of the two suites will
 * tell you that you have made them disagree. ──
 *
 * The canvas needs these answers per keystroke and per drag frame, which is the
 * other reason they live here: a round trip per mouse-move to ask whether a
 * drop is legal would make refusals arrive after the fact, and the design is
 * firm that the DAG is enforced by what the hand can do, not by an error later.
 */
import type { WorkflowEdge, WorkflowGraph, WorkflowNode, WorkflowNodeKind } from '../../../api/forms';

/**
 * The outputs each kind has. A branch's two are named; an ending has none.
 * A branch can carry more paths between its two — `portsOf` is the answer
 * for one node.
 */
export const PORTS: Record<WorkflowNodeKind, string[]> = {
  start: ['then'],
  deliver: ['then'],
  mint: ['then'],
  chase: ['then'],
  branch: ['yes', 'no'],
  merge: ['then'],
  end: [],
};

/**
 * A branch path after the first — see the server's `BranchPath` for why they
 * live in `config.extraPaths` beside the untouched `when`/`labels`.
 */
export interface BranchPath {
  port: string;
  when: unknown;
  label: string;
}

/** Most paths a branch can have, "everything else" included. */
export const MAX_BRANCH_PATHS = 12;

const FIXED_PORTS = new Set(['then', 'yes', 'no']);
const PORT_NAME = /^[A-Za-z][A-Za-z0-9_-]{0,31}$/;

/** A branch's further paths, in the order they are tested. Mirrors the server. */
export function extraPathsOf(node: Pick<WorkflowNode, 'kind' | 'config'>): BranchPath[] {
  if (node.kind !== 'branch') return [];
  const raw = (node.config || {}).extraPaths;
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: BranchPath[] = [];
  for (const p of raw) {
    if (!p || typeof p !== 'object' || Array.isArray(p)) continue;
    const row = p as Record<string, unknown>;
    const port = String(row.port || '').trim();
    if (!PORT_NAME.test(port) || FIXED_PORTS.has(port) || seen.has(port)) continue;
    seen.add(port);
    out.push({ port, when: row.when ?? null, label: typeof row.label === 'string' ? row.label : '' });
  }
  return out;
}

/** The outputs THIS node has, left to right. Mirrors the server. */
export function portsOf(node: Pick<WorkflowNode, 'kind' | 'config'>): string[] {
  if (node.kind !== 'branch') return PORTS[node.kind];
  return ['yes', ...extraPathsOf(node).map((p) => p.port), 'no'];
}

/**
 * A port name for a new path: `p2`, `p3`… — the lowest not `taken`, so the
 * stored drawing stays readable.
 *
 * `taken` must include the ports any arrow still leaves from, not only the
 * paths still listed: a path removed in an editor that has not saved yet
 * keeps its arrow until the save, and a new path reusing its name would
 * inherit that arrow.
 */
export function nextPathPort(taken: Iterable<string>): string {
  const used = new Set(taken);
  let n = 2;
  while (used.has(`p${n}`)) n += 1;
  return `p${n}`;
}

const ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth',
  'ninth', 'tenth', 'eleventh', 'twelfth'];
/** 2 → "second": what an unlabelled path is called, counting from one. */
export const ordinalWord = (n: number): string => ORDINALS[n - 1] || `${n}th`;

/** One exit of a branch: what the edge carries, and the words the card shows. */
export interface BranchExit {
  port: string;
  /** the author's words, or '' */
  label: string;
  /** what the card says where there are none */
  fallback: string;
}

/** A branch's exits, left to right: the first path, any further ones, everything else. */
export function branchExits(node: Pick<WorkflowNode, 'kind' | 'config'>): BranchExit[] {
  const labels = (node.config?.labels as string[] | undefined) || [];
  return [
    { port: 'yes', label: labels[0] || '', fallback: 'the first path' },
    ...extraPathsOf(node).map((p, i) => ({
      port: p.port, label: p.label, fallback: `the ${ordinalWord(i + 2)} path`,
    })),
    { port: 'no', label: labels[1] || '', fallback: 'everything else' },
  ];
}

/**
 * Edges that leave from an output the node no longer has — a branch path
 * somebody just removed. Dropped with the path, in the same change, so the
 * canvas never draws an arrow from nowhere (the server would drop it on save).
 */
export function withoutDeadPorts(g: WorkflowGraph): WorkflowGraph {
  const nodes = byId(g);
  const edges = g.edges.filter((e) => {
    const from = nodes.get(e.from);
    return !from || portsOf(from).includes(e.port);
  });
  return edges.length === g.edges.length ? g : { ...g, edges };
}

/** The one kind whose input accepts more than one edge — see the server's note. */
export const MULTI_INPUT_KINDS: WorkflowNodeKind[] = ['merge'];

/** Kinds that point at a connection, where all their configuration lives. */
export const DELIVERING_KINDS: WorkflowNodeKind[] = ['deliver', 'mint'];

export interface GraphProblem { message: string; nodeId?: string; edge?: WorkflowEdge }
export interface GraphProblems { errors: GraphProblem[]; warnings: GraphProblem[] }

export const byId = (g: WorkflowGraph) => new Map(g.nodes.map((n) => [n.id, n]));
export const edgesInto = (g: WorkflowGraph, id: string) => g.edges.filter((e) => e.to === id);
export const edgesOutOf = (g: WorkflowGraph, id: string, port?: string) =>
  g.edges.filter((e) => e.from === id && (port === undefined || e.port === port));

/**
 * Run order: topological, ties broken by vertical position and then by id.
 *
 * The canvas prints this as a numeral on every card, so it is not an internal
 * detail — it is the claim the page makes about itself. Dragging a node above
 * its sibling has to change it, or the numerals are decoration.
 */
export function topoOrder(g: WorkflowGraph): WorkflowNode[] {
  const nodes = byId(g);
  const indegree = new Map<string, number>(g.nodes.map((n) => [n.id, 0]));
  for (const e of g.edges) indegree.set(e.to, (indegree.get(e.to) || 0) + 1);
  const ahead = (a: WorkflowNode, b: WorkflowNode) =>
    a.y - b.y || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  const ready = g.nodes.filter((n) => !indegree.get(n.id)).sort(ahead);
  const out: WorkflowNode[] = [];
  while (ready.length) {
    const node = ready.shift()!;
    out.push(node);
    for (const e of g.edges) {
      if (e.from !== node.id) continue;
      const left = (indegree.get(e.to) || 0) - 1;
      indegree.set(e.to, left);
      if (left !== 0) continue;
      const next = nodes.get(e.to);
      if (next) { ready.push(next); ready.sort(ahead); }
    }
  }
  return out;
}

/**
 * The run-order numeral each node carries — steps only.
 *
 * `start`, `branch`, `merge` and `end` are counted over: a numeral on a card
 * means "this is the Nth thing that happens to your submission", and a branch
 * is not a thing that happens, it is which way it goes.
 */
export function runNumbers(g: WorkflowGraph): Record<string, number> {
  const out: Record<string, number> = {};
  let n = 0;
  for (const node of topoOrder(g)) {
    if (!DELIVERING_KINDS.includes(node.kind)) continue;
    out[node.id] = ++n;
  }
  return out;
}

/** Every node reachable from `start` by following edges forward. */
export function reachable(g: WorkflowGraph): Set<string> {
  const start = g.nodes.find((n) => n.kind === 'start');
  const seen = new Set<string>();
  if (!start) return seen;
  const stack = [start.id];
  while (stack.length) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const e of g.edges) if (e.from === id) stack.push(e.to);
  }
  return seen;
}

/**
 * Everything upstream of a node, nearest first.
 *
 * This is what the editor's ancestry strip draws and what scopes its token
 * picker: a step may only name what its ancestors published. Below a rejoin
 * the honest set is what EVERY arm supplies, so the walk keeps only nodes
 * reachable on all incoming edges — see `ancestorConnections`.
 */
export function ancestorPath(g: WorkflowGraph, id: string): WorkflowNode[] {
  const nodes = byId(g);
  const out: WorkflowNode[] = [];
  const seen = new Set<string>([id]);
  let cursor = id;
  for (;;) {
    const arrivals = edgesInto(g, cursor);
    if (arrivals.length !== 1) break;   // start, an orphan, or a rejoin
    const parent = nodes.get(arrivals[0].from);
    if (!parent || seen.has(parent.id)) break;
    seen.add(parent.id);
    out.push(parent);
    cursor = parent.id;
  }
  return out.reverse();
}

/**
 * The connections a node may name in a template, as ids.
 *
 * Walks up every incoming edge. At a rejoin it takes the INTERSECTION of the
 * arms, because a token only one arm publishes comes out blank for the
 * submissions that took the other one — which is precisely the class of bug
 * the canvas exists to make visible, and it would be perverse to introduce a
 * new way to hit it.
 */
export function ancestorConnections(g: WorkflowGraph, id: string, seen = new Set<string>()): Set<number> {
  if (seen.has(id)) return new Set();
  seen.add(id);
  const nodes = byId(g);
  const arrivals = edgesInto(g, id);
  const perArm = arrivals.map((e) => {
    const parent = nodes.get(e.from);
    if (!parent) return new Set<number>();
    const up = ancestorConnections(g, parent.id, new Set(seen));
    if (parent.connectionId) up.add(parent.connectionId);
    return up;
  });
  if (!perArm.length) return new Set();
  return perArm.reduce((acc, one) => new Set([...acc].filter((x) => one.has(x))));
}

/** The nearest `mint` above this node, which is what a chase re-runs from. */
export function nearestMint(g: WorkflowGraph, id: string): WorkflowNode | null {
  for (const n of [...ancestorPath(g, id)].reverse()) if (n.kind === 'mint') return n;
  return null;
}

/**
 * Would this edge close a loop? Asked while the mouse is still down.
 *
 * A workflow only runs forward, so the refusal has to happen at the hand — an
 * error after the drop would mean the canvas briefly drew something that could
 * never run.
 */
export const wouldCycle = (g: WorkflowGraph, from: string, to: string): boolean =>
  from === to || descendants(g, to).has(from);

export function descendants(g: WorkflowGraph, id: string): Set<string> {
  const seen = new Set<string>();
  const stack = [id];
  while (stack.length) {
    const at = stack.pop()!;
    for (const e of g.edges) {
      if (e.from !== at || seen.has(e.to)) continue;
      seen.add(e.to);
      stack.push(e.to);
    }
  }
  return seen;
}

/** Why this drop is refused, in one line, or null when it is allowed. */
export function refuseEdge(g: WorkflowGraph, from: string, port: string, to: string): string | null {
  const nodes = byId(g);
  const source = nodes.get(from);
  const target = nodes.get(to);
  if (!source || !target) return 'That step is no longer here.';
  if (from === to) return 'A step cannot lead to itself.';
  if (target.kind === 'start') return 'Nothing runs before the submission itself.';
  if (!portsOf(source).includes(port)) return 'That step has no output there.';
  if (g.edges.some((e) => e.from === from && e.port === port && e.to === to)) {
    return 'These two are already joined.';
  }
  if (g.edges.some((e) => e.from === from && e.port === port)) {
    return 'That output already leads somewhere. Remove the existing arrow first.';
  }
  // Checked BEFORE the occupied-input rule, because dragging back to something
  // upstream trips both and only one of the two reasons tells you anything: a
  // rejoin is the answer to an occupied input, and it is not the answer here.
  if (wouldCycle(g, from, to)) return 'This would loop — a workflow only runs forward.';
  if (!MULTI_INPUT_KINDS.includes(target.kind) && edgesInto(g, to).length) {
    return 'That step already has something before it. Use a “paths rejoin” step to bring two paths together.';
  }
  return null;
}


/**
 * What a step's configuration NAMES, and what a step PUBLISHES.
 *
 * Both are supplied by the caller rather than read here, because the two
 * workspaces know them from different places — the server from `findSpec` and
 * the connection row, the browser from the catalog it already fetched. Keeping
 * them out of this file is what lets the same checks run in both.
 */
export interface GraphContext {
  /** Token names a step publishes for the steps below it, by connection id. */
  publishes?: (connectionId: number) => string[];
  /** Token names a step's own configuration uses, by connection id. */
  uses?: (connectionId: number) => string[];
  /**
   * Does a LIVE submission carry this answer?
   *
   * Published fields only. A branch is evaluated against submissions that have
   * already arrived, and those hold what was published when they were sent —
   * so a condition naming a field that exists only in the draft matches
   * nothing, and quietly sends everybody down the other path.
   */
  fieldIsLive?: (key: string) => boolean;
}

/**
 * Every token any step on this form could publish.
 *
 * A workflow's templates are full of `{{companyName}}` and `{{submissionId}}`,
 * which are answers and facts, not results — so a token is only CHECKED when
 * some step somewhere could have produced it. Anything else is somebody's
 * field key and none of this file's business. The bias is deliberate:
 * over-reporting here would block a save on a form that works.
 */
function publishableTokens(g: WorkflowGraph, ctx: GraphContext): Set<string> {
  const all = new Set<string>();
  if (!ctx.publishes) return all;
  for (const n of g.nodes) {
    if (!n.connectionId) continue;
    for (const t of ctx.publishes(n.connectionId)) all.add(t);
  }
  return all;
}

/**
 * The tokens a step is guaranteed to have when it runs.
 *
 * The INTERSECTION at a rejoin, not the union: a token only one arm published
 * is blank for the submissions that took the other one, and a template naming
 * it would come out empty for half the people who filled the form in. That is
 * bug #2 on this playbook's list, and it is the reason this check exists.
 */
function availableAt(
  g: WorkflowGraph, id: string, ctx: GraphContext, seen = new Set<string>()
): Set<string> {
  if (seen.has(id)) return new Set();
  seen.add(id);
  const byNode = new Map(g.nodes.map((n) => [n.id, n]));
  const arrivals = g.edges.filter((e) => e.to === id);
  const perArm = arrivals.map((e) => {
    const parent = byNode.get(e.from);
    if (!parent) return new Set<string>();
    const up = availableAt(g, parent.id, ctx, new Set(seen));
    if (parent.connectionId && ctx.publishes) {
      for (const t of ctx.publishes(parent.connectionId)) up.add(t);
    }
    return up;
  });
  if (!perArm.length) return new Set();
  return perArm.reduce((acc, one) => new Set([...acc].filter((x) => one.has(x))));
}

/**
 * Every node upstream of this one, on every path that reaches it.
 *
 * Used to answer "is there a mint above this ending?" — and deliberately the
 * UNION rather than the intersection, because the question is whether a link
 * could exist at all, not whether one is guaranteed. The stricter intersection
 * rule is what scopes a template's token picker; F5 applies it to token use.
 */
export function ancestorsOf(g: WorkflowGraph, id: string): Set<string> {
  const seen = new Set<string>();
  const stack = [id];
  while (stack.length) {
    const at = stack.pop()!;
    for (const e of g.edges) {
      if (e.to !== at || seen.has(e.from)) continue;
      seen.add(e.from);
      stack.push(e.from);
    }
  }
  return seen;
}

/**
 * Is this a graph the engine can run?
 *
 * Structural only, matching the server. The design-time checks people feel —
 * a token used before anything published it, a path with no ending — are F5's
 * and belong in `warnings` beside these.
 */
export function validateGraph(g: WorkflowGraph, ctx: GraphContext = {}): GraphProblems {
  const errors: GraphProblem[] = [];
  const warnings: GraphProblem[] = [];
  const nodes = byId(g);

  const starts = g.nodes.filter((n) => n.kind === 'start');
  if (!starts.length) errors.push({ message: 'This workflow has no starting point.' });
  for (const s of starts.slice(1)) {
    errors.push({ nodeId: s.id, message: 'A workflow has one starting point — a submission only happens once.' });
  }
  for (const n of g.nodes) {
    if (DELIVERING_KINDS.includes(n.kind) && !n.connectionId) {
      errors.push({ nodeId: n.id, message: 'This step has nothing to run — open it and choose where it sends.' });
    }
  }

  const arrivals = new Map<string, number>();
  for (const e of g.edges) {
    if (nodes.get(e.to)?.kind === 'start') {
      errors.push({ edge: e, nodeId: e.to, message: 'Nothing runs before the submission itself.' });
    }
    arrivals.set(e.to, (arrivals.get(e.to) || 0) + 1);
  }
  for (const [to, count] of arrivals) {
    const kind = nodes.get(to)?.kind;
    if (count > 1 && kind && !MULTI_INPUT_KINDS.includes(kind)) {
      errors.push({
        nodeId: to,
        message: 'Two steps lead into this one. Put a "paths rejoin" step here, or give each path its own.',
      });
    }
  }
  for (const n of g.nodes) {
    if (n.kind === 'merge' && (arrivals.get(n.id) || 0) < 2) {
      warnings.push({ nodeId: n.id, message: 'Only one path reaches this rejoin, so it joins nothing.' });
    }
  }

  // "Continue into the follow-up form" with nothing above it that mints a
  // link. This is the canvas's flagship error, and the one the whole page was
  // drawn to prevent: today it is a thank-you page whose Continue button has a
  // blank href, discovered by a customer, days later, with nothing in any log.
  for (const n of g.nodes) {
    if (n.kind !== 'end' || (n.config || {}).mode !== 'continue') continue;
    const above = ancestorsOf(g, n.id);
    if (!g.nodes.some((m) => m.kind === 'mint' && above.has(m.id))) {
      errors.push({
        nodeId: n.id,
        message: 'This ending continues into another form, but no step above it makes the link.',
      });
    }
  }

  if (topoOrder(g).length !== g.nodes.length) {
    errors.push({ message: 'This workflow loops back on itself — it would never finish.' });
  }

  // ---- what a step is allowed to name -----------------------------------
  // The bug this exists for: {{inviteUrl}} came out blank in a customer's
  // email because the invite has to run BEFORE the connection carrying it, a
  // fact nothing on screen said. Now it is an error at design time.
  const publishable = publishableTokens(g, ctx);
  if (ctx.uses && publishable.size) {
    for (const n of g.nodes) {
      if (!n.connectionId) continue;
      const have = availableAt(g, n.id, ctx);
      for (const token of ctx.uses(n.connectionId)) {
        if (!publishable.has(token) || have.has(token)) continue;
        errors.push({
          nodeId: n.id,
          message: `Uses {{${token}}}, but no step above it publishes one.`,
        });
      }
    }
  }

  // ---- warnings: a piece that would do nothing ---------------------------
  const live = reachable(g);
  for (const n of g.nodes) {
    if (n.kind === 'start' || live.has(n.id)) continue;
    warnings.push({ nodeId: n.id, message: 'Nothing leads to this step, so it will never run.' });
  }

  for (const n of g.nodes) {
    if (n.kind !== 'branch') continue;
    const extra = extraPathsOf(n);
    for (const port of portsOf(n)) {
      if (g.edges.some((e) => e.from === n.id && e.port === port)) continue;
      const i = extra.findIndex((p) => p.port === port);
      const label = i >= 0
        ? extra[i].label || ordinalWord(i + 2)
        : (n.config?.labels as string[] | undefined)?.[port === 'yes' ? 0 : 1]
          || (port === 'yes' ? 'first' : 'other');
      warnings.push({
        nodeId: n.id,
        message: `Nothing happens on the “${label}” path.`,
      });
    }
  }

  // A path that stops without an ending falls back to the form's own
  // thank-you — a page nobody wrote for that path. The canvas draws a ghost
  // stadium there; this is the same fact, in the problems list.
  for (const n of g.nodes) {
    if (n.kind === 'end' || !live.has(n.id)) continue;
    for (const port of portsOf(n)) {
      if (g.edges.some((e) => e.from === n.id && e.port === port)) continue;
      if (n.kind === 'branch') continue;   // already said, above, in better words
      warnings.push({
        nodeId: n.id,
        message: 'This path has no ending, so it falls back to the form’s default thank-you.',
      });
    }
  }


  // A branch testing an answer no live submission carries. Not an error —
  // building a flow ahead of publishing the field is a reasonable thing to be
  // doing — but it decides every submission until the form is published, and
  // that is exactly the kind of fact this page exists to put on screen.
  if (ctx.fieldIsLive) {
    for (const n of g.nodes) {
      if (n.kind !== 'branch') continue;
      const extra = extraPathsOf(n);
      // Each question once, across every path — see the server's note.
      const tested = new Set<string>();
      for (const group of [n.config?.when, ...extra.map((p) => p.when)]) {
        for (const row of ((group as { when?: { field: string }[] } | null | undefined)?.when) || []) {
          if (row?.field) tested.add(row.field);
        }
      }
      for (const field of tested) {
        if (ctx.fieldIsLive(field)) continue;
        warnings.push({
          nodeId: n.id,
          message: `Tests “${field}”, which this form has not published yet — `
            + (extra.length
              ? 'until it does, no submission matches a path that tests it.'
              : 'until it does, every submission takes the other path.'),
        });
      }
    }
  }

  return { errors, warnings };
}

/**
 * The connections this form has that the drawing does not.
 *
 * With the Connections tab gone, one of these is invisible: nowhere to see it,
 * edit it or delete it. It does not run either, because the graph is what
 * runs. The canvas says both halves out loud and offers to put them on the
 * canvas, rather than letting a form quietly carry something nobody can find.
 */
export function undrawnConnections<T extends { id: number }>(g: WorkflowGraph, connections: T[]): T[] {
  const drawn = new Set(g.nodes.map((n) => n.connectionId).filter(Boolean));
  return connections.filter((c) => !drawn.has(c.id));
}

/**
 * Would deleting this step leave its connection with no step at all?
 *
 * If so the connection is deleted with it, because an invisible connection is
 * the very thing this page exists to end. If another step still points at the
 * same connection — two paths that want the same email, which the design
 * explicitly blesses — the settings stay, because the other step needs them.
 */
export function connectionIsOrphaned(g: WorkflowGraph, nodeId: string): boolean {
  const node = g.nodes.find((n) => n.id === nodeId);
  if (!node?.connectionId) return false;
  return !g.nodes.some((n) => n.id !== nodeId && n.connectionId === node.connectionId);
}

/**
 * The other steps running the same connection as this one.
 *
 * Drawn before "Add another like this" made a copy, when it shared instead.
 * They are one set of settings shown in several places, so an edit to any is
 * an edit to all — which the editor has to say, and the menu has to undo.
 */
export function sharingSteps(g: WorkflowGraph, nodeId: string): WorkflowNode[] {
  const node = g.nodes.find((n) => n.id === nodeId);
  if (!node?.connectionId) return [];
  return g.nodes.filter((n) => n.id !== nodeId && n.connectionId === node.connectionId);
}

/**
 * The tails of paths that reach no ending.
 *
 * The canvas draws a dashed ghost stadium below each of these, because today
 * such a path falls back to the form's own thank-you — a page nobody wrote for
 * THAT path — and nothing anywhere says so. Making the fallback visible is the
 * whole point; clicking the ghost turns it into a real ending.
 *
 * A node counts as a tail when it is reachable, is not itself an ending, and
 * has an unused output. A branch with one arm drawn has an unused output and
 * genuinely is an open path.
 */
export function openPathTails(g: WorkflowGraph): { node: WorkflowNode; port: string }[] {
  const live = reachable(g);
  const out: { node: WorkflowNode; port: string }[] = [];
  for (const n of g.nodes) {
    if (n.kind === 'end' || !live.has(n.id)) continue;
    for (const port of portsOf(n)) {
      if (!edgesOutOf(g, n.id, port).length) out.push({ node: n, port });
    }
  }
  return out;
}

/** A fresh node id. Generated here so an unsaved node is addressable at once. */
let seq = 0;
export const newNodeId = () => `n${Date.now().toString(36)}${(seq += 1).toString(36)}`;

/**
 * `freshdesk` + `ticketId` -> `freshdeskTicketId`. Mirrors the server's
 * `prefixTokens`, which is what actually names them at delivery time.
 */
export const tokenName = (type: string, key: string): string =>
  type.replace(/[^a-zA-Z0-9]/g, '') + key.charAt(0).toUpperCase() + key.slice(1);

/**
 * What the bespoke handlers publish, which no catalog spec declares.
 *
 * Only the invite, and deliberately — see the server's copy in
 * `services/delivery/tokens.ts`. Under-reporting costs a warning nobody gets;
 * over-reporting refuses to save a workflow that works.
 */
const BESPOKE_PROVIDES: Record<string, string[]> = {
  invite: ['url', 'expiresAt', 'email'],
};

/** Every `{{token}}` a connection's configuration names. */
export function tokensUsedIn(config: unknown): string[] {
  const text = JSON.stringify(config ?? {});
  const found = new Set<string>();
  for (const m of text.matchAll(/\{\{\s*([A-Za-z0-9_.]+)\s*\}\}/g)) found.add(m[1]);
  return [...found];
}

/** The two lookups `validateGraph` needs, bound to this form. */
export function graphContextFor(
  connections: { id: number; type: string; config?: Record<string, unknown> }[],
  catalog: { type: string; provides?: { key: string }[] }[],
  /** Keys a live submission carries — published fields, not drafted ones. */
  liveFieldKeys?: Set<string>
): GraphContext {
  const byId = new Map(connections.map((c) => [c.id, c]));
  const specOf = (type: string) => catalog.find((c) => c.type === type);
  return {
    publishes: (id) => {
      const conn = byId.get(id);
      if (!conn) return [];
      const declared = specOf(conn.type)?.provides?.map((p) => p.key) || [];
      const keys = declared.length ? declared : (BESPOKE_PROVIDES[conn.type] || []);
      return keys.map((k) => tokenName(conn.type, k));
    },
    uses: (id) => {
      const conn = byId.get(id);
      return conn ? tokensUsedIn(conn.config) : [];
    },
    // Omitted rather than answered "no" when the caller does not know which
    // fields are live: a checker that has not been told cannot warn honestly.
    ...(liveFieldKeys ? { fieldIsLive: (key: string) => liveFieldKeys.has(key.split('.')[0]) } : {}),
  };
}

/**
 * The path a submission took, replayed from its answers.
 *
 * Nothing about which nodes ran is stored — the playbook is firm that the run
 * view reads back from the delivery rows the pipeline already writes and adds
 * no new table. So the path is recomputed the way the engine computed it: walk
 * from `start`, and at each branch ask its condition of the answers this
 * submission actually gave.
 *
 * The honest caveat, and the reason `changedSince` exists: the DRAWING is the
 * current one. A workflow edited since a submission ran is replayed as it
 * stands today, which can differ from what happened. The drawer says so rather
 * than presenting a reconstruction as a recording.
 */
export interface TakenPath {
  /** Node ids the submission passed through. */
  nodes: Set<string>;
  /** `nodeId\nport` for each edge it followed. */
  ports: Set<string>;
  /** The ending it reached, if any. */
  endedAt: string | null;
}

export function replayPath(
  g: WorkflowGraph,
  data: Record<string, unknown>,
  branchPort: (node: WorkflowNode, data: Record<string, unknown>) => string
): TakenPath {
  const nodes = new Set<string>();
  const ports = new Set<string>();
  let endedAt: string | null = null;

  for (const node of topoOrder(g)) {
    const arrived = node.kind === 'start'
      || g.edges.some((e) => e.to === node.id && ports.has(`${e.from}\n${e.port}`));
    if (!arrived) continue;
    nodes.add(node.id);
    if (node.kind === 'end') { endedAt = endedAt || node.id; continue; }
    ports.add(`${node.id}\n${node.kind === 'branch' ? branchPort(node, data) : 'then'}`);
  }
  return { nodes, ports, endedAt };
}

/**
 * Which way a branch sent this submission. Mirrors the server's `branchPort`.
 *
 * A branch with no readable condition takes the second path — deliberately not
 * the absent-`run_when` rule, which means "always". A connection defaulting to
 * running is safe; a branch defaulting to a direction is not. Further paths
 * are asked in order after the first; `no` takes what none of them did.
 */
export function branchPortFor(
  node: WorkflowNode,
  data: Record<string, unknown>,
  holds: (group: unknown, data: Record<string, unknown>) => boolean
): string {
  if (holds((node.config || {}).when, data)) return 'yes';
  for (const path of extraPathsOf(node)) if (holds(path.when, data)) return path.port;
  return 'no';
}

/**
 * Where a newly added step should attach, and what it displaces.
 *
 * "Add this below the thing I am looking at" is what the gesture means, so a
 * free output on the SELECTED step wins. When that step's output already leads
 * somewhere the new step is SPLICED in — the existing arrow moves to it —
 * because adding a step in the middle of a flow should be one gesture, not one
 * gesture plus two arrow-redraws.
 *
 * The `end` case is why this is a function rather than a line. An ending has
 * NO outputs, so reaching for "its first port" produced an edge from a port
 * that does not exist: the server dropped it on save and the step arrived
 * unattached with nothing saying why. Now an ending falls through to the first
 * open tail, and failing that to null — which lands the step unattached for
 * the author to wire up, and is better than inventing a place for it.
 */
export function attachmentFor(
  g: WorkflowGraph, preferred?: string | null
): { from: string; port: string; displaces?: string } | null {
  const free = (n: WorkflowNode) => portsOf(n).find((p) => !edgesOutOf(g, n.id, p).length);
  const chosen = preferred ? g.nodes.find((n) => n.id === preferred) : undefined;
  if (chosen) {
    const spare = free(chosen);
    if (spare) return { from: chosen.id, port: spare };
    const port = portsOf(chosen)[0];
    if (port) {
      const existing = edgesOutOf(g, chosen.id, port)[0];
      if (existing) return { from: chosen.id, port, displaces: existing.to };
    }
  }
  const live = reachable(g);
  for (const n of g.nodes) {
    if (n.kind === 'end' || !live.has(n.id)) continue;
    const spare = free(n);
    if (spare) return { from: n.id, port: spare };
  }
  return null;
}

/**
 * The step an arrow key should move the selection to.
 *
 * Down and up walk the flow: the step this one leads to, and the step that
 * leads to it. Left and right cross between siblings — the arms of a branch,
 * or anything else drawn side by side — by taking the nearest card in that
 * direction. Nothing here invents a route the drawing does not have.
 */
export function neighbourOf(
  g: WorkflowGraph, id: string, dir: 'up' | 'down' | 'left' | 'right'
): string | null {
  const here = g.nodes.find((n) => n.id === id);
  if (!here) return null;

  if (dir === 'down') {
    const out = g.edges.filter((e) => e.from === id).map((e) => e.to);
    return pick(g, out, here, (n) => n.y) ?? null;
  }
  if (dir === 'up') {
    const into = g.edges.filter((e) => e.to === id).map((e) => e.from);
    return pick(g, into, here, (n) => -n.y) ?? null;
  }
  // Sideways: the nearest card that way, preferring ones at a similar height,
  // so crossing a branch's arms works without knowing they are arms.
  const wanted = dir === 'left' ? -1 : 1;
  const candidates = g.nodes.filter((n) => n.id !== id && Math.sign(n.x - here.x) === wanted);
  if (!candidates.length) return null;
  return candidates.reduce((best, n) => {
    const cost = (c: WorkflowNode) => Math.abs(c.y - here.y) * 3 + Math.abs(c.x - here.x);
    return cost(n) < cost(best) ? n : best;
  }).id;
}

/** Of these ids, the one the given ordering puts first. */
function pick(
  g: WorkflowGraph, ids: string[], from: WorkflowNode, rank: (n: WorkflowNode) => number
): string | undefined {
  const nodes = ids.map((i) => g.nodes.find((n) => n.id === i)).filter(Boolean) as WorkflowNode[];
  if (!nodes.length) return undefined;
  // Among several, the one most nearly straight ahead — an arrow key should
  // follow the line somebody is reading down.
  return nodes.reduce((best, n) => (
    rank(n) - Math.abs(n.x - from.x) / 100 > rank(best) - Math.abs(best.x - from.x) / 100 ? best : n
  )).id;
}

/**
 * Why this step cannot be removed, or null when it can be.
 *
 * Every workflow has a floor: a submission arrives, and it ends somewhere.
 * Those two are not steps somebody added, they are the shape of what a form
 * does — and the ending in particular is what the person who filled the form
 * in actually sees, so a canvas that let you delete the last one would let you
 * delete the thank-you screen with nothing to put in its place.
 */
export function undeletable(g: WorkflowGraph, id: string): string | null {
  const node = g.nodes.find((n) => n.id === id);
  if (!node) return null;
  if (node.kind === 'start') {
    return 'Every workflow starts with a submission arriving — that is not a step, it is the form.';
  }
  if (node.kind === 'end' && g.nodes.filter((n) => n.kind === 'end').length === 1) {
    return 'Every workflow ends somewhere, and this is the only ending. '
      + 'Add another before removing this one.';
  }
  return null;
}

/**
 * Put a new step after the selection (R6): wired in where the edge rules
 * allow, unattached where they do not.
 *
 * `attach` is `attachmentFor`'s answer. A free output on the anchor takes the
 * new step; an occupied one is SPLICED — the anchor's arrow moves onto the new
 * step and the new step's first output takes over what the anchor led to —
 * and everything below the splice is pushed down by the new card's height so
 * nothing lands on top of anything. Each arrow is checked with `refuseEdge`,
 * the same rule a drag is held to: an arrow a drag would refuse is not drawn
 * here either, and the step simply lands unattached for the author to wire.
 *
 * A step with no output (an ending) never splices: taking the anchor's arrow
 * away from a subtree it cannot lead back to would orphan that subtree, which
 * is worse than a loose card.
 */
export function insertNode(
  g: WorkflowGraph,
  node: WorkflowNode,
  attach: { from: string; port: string; displaces?: string } | null,
  opts: { height?: number; gap?: number } = {}
): WorkflowGraph {
  const height = opts.height ?? 60;
  const gap = opts.gap ?? 50;
  let nodes = [...g.nodes, node];
  const edges = [...g.edges];
  if (!attach) return { nodes, edges };

  const onward = portsOf(node)[0];
  if (attach.displaces) {
    if (!onward) return { nodes, edges };
    // Check both arrows against the graph WITHOUT the anchor's old arrow,
    // which is what the graph will be once it has moved.
    const without = { nodes, edges: edges.filter((e) => !(e.from === attach.from && e.port === attach.port)) };
    if (refuseEdge(without, attach.from, attach.port, node.id)) return { nodes, edges };
    const withIn = { nodes, edges: [...without.edges, { from: attach.from, port: attach.port, to: node.id }] };
    if (refuseEdge(withIn, node.id, onward, attach.displaces)) return { nodes, edges };
    // Push what hangs below the splice down out of the new card's way — by
    // however much it takes for the displaced step to clear the new one, so a
    // subtree that already has room does not move at all.
    const below = new Set([attach.displaces, ...descendants(g, attach.displaces)]);
    const displaced = g.nodes.find((n) => n.id === attach.displaces);
    const dy = displaced ? Math.max(0, node.y + height + gap - displaced.y) : 0;
    nodes = dy ? nodes.map((n) => (below.has(n.id) && n.id !== node.id ? { ...n, y: n.y + dy } : n)) : nodes;
    return {
      nodes,
      edges: [...withIn.edges, { from: node.id, port: onward, to: attach.displaces }],
    };
  }
  if (refuseEdge({ nodes, edges }, attach.from, attach.port, node.id)) return { nodes, edges };
  return { nodes, edges: [...edges, { from: attach.from, port: attach.port, to: node.id }] };
}

/**
 * The arrow to draw when a step is removed (R6): its one parent to its one
 * child, where the edge rules allow it — so deleting a step in the middle of
 * a flow closes the gap instead of leaving two loose ends. A branch, a step
 * with several parents (a rejoin) or a step leading nowhere gives nothing,
 * and the caller leaves the gap as it always did.
 */
export function relinkAround(g: WorkflowGraph, id: string): WorkflowEdge | null {
  const into = edgesInto(g, id);
  const out = g.edges.filter((e) => e.from === id);
  if (into.length !== 1 || out.length !== 1) return null;
  const parent = into[0];
  const child = out[0];
  const rest: WorkflowGraph = {
    nodes: g.nodes.filter((n) => n.id !== id),
    edges: g.edges.filter((e) => e.from !== id && e.to !== id),
  };
  if (refuseEdge(rest, parent.from, parent.port, child.to)) return null;
  return { from: parent.from, port: parent.port, to: child.to };
}
